import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnDestroy,
  ViewChild,
  OnChanges,
  ElementRef,
  HostListener,
  SimpleChanges,
} from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { RegionsService } from '../../services/regions.service'
import { Observable, BehaviorSubject, combineLatest } from 'rxjs'
import { map } from 'rxjs/operators'
import { ApplicationType } from 'src/app/services/dtos'

@Component({
  selector: 'app-region-dropdown',
  templateUrl: './region-dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RegionDropdownComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class RegionDropdownComponent implements ControlValueAccessor, OnDestroy, OnChanges {
  @Input() placeholder = 'Region'
  @Input() disabled = false
  @Input() excludeRegion: string = ''
  @Input() allowSameRegion = false
  @Input() compareAccount: string = ''
  @Input() currentAccount: string = ''
  @Input() extraClasses: string = ''
  @Input() applicationType: ApplicationType = null

  @Output() regionSelected = new EventEmitter<string>()

  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef<HTMLInputElement>
  @ViewChild('dropdownContainer', { static: true }) dropdownContainer!: ElementRef<HTMLDivElement>

  dropdownOpen = false
  value = ''
  searchTerm = ''
  searchTerm$ = new BehaviorSubject<string>('')
  possibleRegions$: Observable<{ label: string; value: string }[]>
  filteredRegions$: Observable<{ label: string; value: string }[]>

  private onChange = (value: any) => {}
  private onTouched = () => {}

  constructor(private regionsService: RegionsService) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.dropdownOpen && this.dropdownContainer) {
      const target = event.target as HTMLElement
      const clickedInside = this.dropdownContainer.nativeElement.contains(target)

      if (!clickedInside) {
        this.dropdownOpen = false
        this.clearSearch()
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.possibleRegions$ = this.regionsService.getRegionsFormatted(this.applicationType)

    if (changes['applicationType']) {
      // Clear selection
      this.selectRegion({ label: '', value: '' })
    }

    // Check if account bindings changed and current selection is now invalid
    if (changes['compareAccount'] || changes['currentAccount'] || changes['allowSameRegion']) {
      if (this.value && this.isRegionDisabled({ label: '', value: this.value })) {
        // Current selection is now invalid - clear it
        this.selectRegion({ label: '', value: '' })
      }
    }

    // Set up filtered regions based on search term
    this.filteredRegions$ = combineLatest([this.possibleRegions$, this.searchTerm$]).pipe(
      map(([regions, searchTerm]) => {
        if (!searchTerm.trim()) {
          return regions
        }

        const lowerSearchTerm = searchTerm.toLowerCase()
        return regions.filter(
          (region) =>
            region.label.toLowerCase().includes(lowerSearchTerm) || region.value.toLowerCase().includes(lowerSearchTerm)
        )
      })
    )
  }

  ngOnDestroy() {
    // HostListener cleanup is handled automatically by Angular
    this.searchTerm$.complete()
  }

  toggleDropdown() {
    if (this.disabled) return
    this.dropdownOpen = !this.dropdownOpen

    if (this.dropdownOpen) {
      // Clear search term when opening
      this.clearSearch()
      // Focus search input after DOM update
      setTimeout(() => {
        if (this.searchInput) {
          this.searchInput.nativeElement.focus()
        }
      }, 0)
    }
  }

  selectRegion(region: { label: string; value: string }) {
    if (this.disabled || this.isRegionDisabled(region)) return

    this.value = region.value
    this.onChange(this.value)
    this.onTouched()
    this.dropdownOpen = false
    this.clearSearch()
    this.regionSelected.emit(this.value)
  }

  onSearchInput(event: Event) {
    const target = event.target as HTMLInputElement
    this.searchTerm = target.value
    this.searchTerm$.next(this.searchTerm)
  }

  clearSearch() {
    this.searchTerm = ''
    this.searchTerm$.next('')
  }

  onSearchKeydown(event: KeyboardEvent) {
    // Prevent dropdown from closing when typing in search
    event.stopPropagation()

    if (event.key === 'Escape') {
      this.dropdownOpen = false
      return
    }

    if (event.key === 'Enter') {
      // Select first visible region if available
      this.filteredRegions$
        .subscribe((regions) => {
          const firstAvailableRegion = regions.find((region) => !this.isRegionDisabled(region))
          if (firstAvailableRegion) {
            this.selectRegion(firstAvailableRegion)
          }
        })
        .unsubscribe()
      return
    }
  }

  isRegionDisabled(region: { label: string; value: string }): boolean {
    if (!this.excludeRegion) return false

    if (region.value !== this.excludeRegion) return false

    // Same region selected - determine if it's allowed
    // Default to allowing same region if entitlement value is null/undefined

    // Cross-account same-region pairing is controlled by the sameRegionSyncPairs entitlement
    // When enabled, different accounts can sync to the same region
    if (this.compareAccount && this.currentAccount && this.compareAccount !== this.currentAccount) {
      return this.allowSameRegion === false // Cross-account same-region requires the entitlement
    }

    // For same-account scenarios:
    // - Same account + same region = never allowed (not a valid configuration)
    if (this.compareAccount && this.currentAccount && this.compareAccount === this.currentAccount) {
      return true // Always disable same-account same-region
    }

    // When accounts aren't both specified yet:
    // - Check the allowSameRegion flag which is tied to the sameRegionSyncPairs entitlement
    return this.allowSameRegion === false
  }

  getSelectedRegionName(regionCode: string): string {
    if (!regionCode) return ''
    return this.regionsService.getRegionName(this.applicationType, regionCode) ?? regionCode
  }

  getRegionNameOnly(label: string): string {
    // Extract the full region name from "Canada West (Calgary) (ca-west-1)" -> "Canada West (Calgary)"
    // This removes only the final region code in parentheses
    const match = label.match(/^(.+)\s*\([^)]+\)$/)
    return match ? match[1].trim() : label
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.value = value || ''
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }
}
